import { useMemo, useState } from 'react';
import { supabase } from '../lib/supabase';
import { toast } from '../lib/toast';
import { Star, Edit, Trash2, Flame } from 'lucide-react';
import { useEmberStore } from '../lib/store';

interface SessionCardProps {
  session: any; // { id, rating_10, notes, photo_url, created_at, cellar: { brand, blend_name } }
  onDelete?: () => void;
  onEdit?: () => void;
}

export default function SessionCard({ session, onDelete, onEdit }: SessionCardProps) {
  const { currentUser } = useEmberStore();
  const [expanded, setExpanded] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const { rating_10, notes, photo_url, created_at, cellar, tobacco_manual } = session;
  const formattedDate = new Date(created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  const storageLabel = useMemo(() => {
    if (!cellar?.storage_type) return '';
    return cellar.storage_type
      .split(/[_\s-]+/)
      .map((part: string) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }, [cellar?.storage_type]);

  const title = cellar
    ? `${cellar.brand} - ${cellar.blend_name}${storageLabel ? ` (${storageLabel})` : ''}`
    : (tobacco_manual || 'Session');

  async function handleDelete() {
    // Styled confirmation modal (matches existing app modals)
    const modal = document.createElement('div');
    modal.style.cssText = `
      position: fixed;
      top: 0; left: 0; right: 0; bottom: 0;
      background: rgba(0, 0, 0, 0.8);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1000;
      padding: 1rem;
    `;

    const content = document.createElement('div');
    content.style.cssText = `
      background: #1a1a1a;
      border: 1px solid #e4574d;
      border-radius: 12px;
      padding: 2rem;
      max-width: 400px;
      width: 100%;
      text-align: center;
      color: #ffffff;
    `;

    content.innerHTML = `
      <h3 style="font-size: 1.25rem; margin-bottom: 1rem; color: #e4574d;">Delete Session</h3>
      <p style="color: #cfcfcf; margin-bottom: 1.5rem;">Are you sure you want to delete this session? This action cannot be undone.</p>
      <div style="display: flex; gap: 1rem; justify-content: center;">
        <button id="cancel-delete" style="
          padding: 0.75rem 1.5rem;
          background: transparent;
          color: #999;
          border: 1px solid #666;
          border-radius: 6px;
          cursor: pointer;
          font-weight: 600;
        ">Cancel</button>
        <button id="confirm-delete" style="
          padding: 0.75rem 1.5rem;
          background: #e4574d;
          color: #ffffff;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-weight: 600;
        ">Delete</button>
      </div>
    `;

    modal.appendChild(content);
    document.body.appendChild(modal);

    const cancelBtn = content.querySelector('#cancel-delete');
    const confirmBtn = content.querySelector('#confirm-delete');

    cancelBtn?.addEventListener('click', () => modal.remove());
    confirmBtn?.addEventListener('click', async () => {
      modal.remove();
      setDeleting(true);
      try {
        const { error } = await supabase.from('sessions').delete().eq('id', session.id);
        if (error) throw error;
        onDelete?.();
      } catch (err) {
        console.error('Delete session failed', err);
        toast.error('Failed to delete session');
      } finally {
        setDeleting(false);
      }
    });
  }

  const previewHeight = 292;
  const hasPhoto = Boolean(photo_url);
  const backgroundImageValue = hasPhoto ? `url(${photo_url})` : "url('/ember-bg.jpg')";
  const backgroundSizeValue = 'cover';
  const backgroundPositionValue = 'center';
  const backgroundRepeatValue = 'no-repeat';
  const isOwner = currentUser?.id ? currentUser.id === session?.user_id : true;
  const pillClass = isOwner ? 'pill-owner' : 'pill-guest';

  return (
    <div
      className="card"
      onClick={() => setExpanded(!expanded)}
      style={{
        position: 'relative',
        overflow: 'hidden',
        cursor: 'pointer',
        ...(expanded ? {} : { height: `${previewHeight}px` }),
        ...(backgroundImageValue && {
          backgroundColor: 'transparent',
          backgroundImage: backgroundImageValue,
          backgroundSize: backgroundSizeValue,
          backgroundPosition: backgroundPositionValue,
          backgroundRepeat: backgroundRepeatValue,
        }),
      }}
    >
      {backgroundImageValue && (
        <div style={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(to right, rgba(11,11,11,0.92) 0%, rgba(11,11,11,0.75) 50%, rgba(11,11,11,0.20) 80%, transparent 100%)',
          pointerEvents: 'none'
        }} />
      )}

      {!hasPhoto && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1,
            opacity: 0.2,
          }}
        >
          <Flame size={220} color="#0b0b0b" strokeWidth={1.1} />
        </div>
      )}

      {!expanded && (
        <div
          className={`expand-pill ${pillClass}`}
          aria-hidden="true"
        />
      )}

      <div
        style={{ position: 'relative', zIndex: 1, paddingBottom: '2.5rem' }}
      >
        <h3 style={{ fontSize: '1.125rem', marginBottom: '0.25rem', fontWeight: 600, color: '#f5f5f5' }}>
          {title}
        </h3>
        <p style={{ fontSize: '0.875rem', color: '#f5f5f5', marginBottom: '0.75rem' }}>{formattedDate}</p>

        {/* Rating */}
        <div style={{ display: 'flex', gap: '0.2rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
          {Array.from({ length: 10 }).map((_, i) => (
            <Star key={i} size={14} fill={i < (rating_10 || 0) ? '#ff9500' : 'none'} color={i < (rating_10 || 0) ? '#ff9500' : '#6b4423'} />
          ))}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', fontSize: '0.75rem', color: '#f5f5f5' }}>
          <span style={{ opacity: 0.85 }}>Session • Private</span>
        </div>

      </div>

      {expanded && (
        <div style={{ marginTop: '1rem', paddingTop: '1rem', position: 'relative', zIndex: 1 }}>
          {cellar?.image_url && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              marginBottom: '1rem'
            }}>
              <div style={{
                width: '64px',
                height: '64px',
                borderRadius: '8px',
                overflow: 'hidden',
                border: '1px solid var(--color-border)'
              }}>
                <div className="card-photo" style={{ width: '100%', height: '100%' }}>
                  <img
                    src={cellar.image_url}
                    alt="Cellar item"
                    loading="lazy"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                </div>
              </div>
              <div>
                <div style={{ fontSize: '0.85rem', color: '#ccc' }}>Cellar item photo</div>
                {storageLabel && (
                  <div style={{ fontSize: '0.75rem', color: '#888' }}>Storage: {storageLabel}</div>
                )}
              </div>
            </div>
          )}

          {notes && (
            <div style={{ marginBottom: '1rem' }}>
              <h4 style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem', color: 'var(--color-accent)' }}>Notes</h4>
              <p style={{ fontSize: '0.875rem', color: '#ffffff', lineHeight: 1.5 }}>{notes}</p>
            </div>
          )}

          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button onClick={(e) => { e.stopPropagation(); onEdit?.(); }} style={{ flex: 1, padding: '0.5rem', backgroundColor: 'var(--color-card)', border: '1px solid var(--color-accent)', color: 'var(--color-accent)', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', fontSize: '0.875rem', fontWeight: 500 }}>
              <Edit size={16} /> Edit
            </button>
            <button onClick={(e) => { e.stopPropagation(); handleDelete(); }} disabled={deleting} style={{ flex: 1, padding: '0.5rem', backgroundColor: 'var(--color-card)', border: '1px solid var(--color-danger)', color: 'var(--color-danger)', borderRadius: '6px', cursor: deleting ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', fontSize: '0.875rem', fontWeight: 500, opacity: deleting ? 0.5 : 1 }}>
              <Trash2 size={16} /> {deleting ? 'Deleting…' : 'Delete'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


